/**
 * FileViewer — universal in-app file preview modal.
 *
 * Fetches /api/files/preview/{file_id} and renders the file inline:
 *   - Native (PDF, image, text, audio, video) → <iframe>/native tag
 *   - Office (.doc, .docx, .xls, .xlsx, .ppt, .pptx, RTF, ODT, ODS, ODP)
 *     → Microsoft Office Online Viewer embed (server returns embed_url)
 *
 * Also supports the `preview_url`/`embed_url` shape used by AI-evidence
 * drafts (which don't live in the `files` collection). Callers just pass
 * one of:
 *   - fileId (uses /api/files/preview/{id})
 *   - directUrl (native inline URL — image/pdf/etc.)
 *   - officeEmbedUrl (Office Online embed URL)
 *   - filename (for the modal title)
 *
 * PRIVACY NOTE: For office files we send a short-lived (60 min) presigned
 * URL to Microsoft's viewer at view.officeapps.live.com so it can render
 * inline. The customer has consented to this by choosing "Microsoft Office
 * Online" as the previewer.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { Loader2, ExternalLink, Download, FileWarning } from 'lucide-react';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

const isImage = (m) => (m || '').startsWith('image/');
const isVideo = (m) => (m || '').startsWith('video/');
const isAudio = (m) => (m || '').startsWith('audio/');
const isPdf = (m) => (m || '').includes('pdf');
const isText = (m) => (m || '').startsWith('text/') || (m || '').includes('json');

// A file we can render fully client-side without shipping the bytes to a
// third-party viewer. Currently just Word .docx (mammoth) — .xlsx/.pptx
// fall through to MS Office Online.
const isDocx = (mime, name) => {
  const m = (mime || '').toLowerCase();
  const n = (name || '').toLowerCase();
  return (
    m === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
    n.endsWith('.docx')
  );
};

export const FileViewer = ({
  open,
  onClose,
  fileId,
  directUrl,
  officeEmbedUrl,
  mime,
  filename,
  title,
}) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  // Client-side .docx renderer state — kept separate so mammoth failures
  // never block the eventual Office-Online fallback.
  const [docxHtml, setDocxHtml] = useState(null);
  const [docxLoading, setDocxLoading] = useState(false);
  const [docxError, setDocxError] = useState('');

  useEffect(() => {
    let cancelled = false;
    if (!open) return undefined;
    // Direct URL mode — no fetch needed
    if (directUrl || officeEmbedUrl) {
      setData({
        preview_type: officeEmbedUrl ? 'ms_office_online' : 'inline',
        preview_mime: mime,
        embed_url: officeEmbedUrl,
        download_url: directUrl || officeEmbedUrl,
        filename: filename || 'document',
      });
      return undefined;
    }
    if (!fileId) return undefined;
    setLoading(true);
    setErr('');
    setData(null);
    setDocxHtml(null);
    setDocxError('');
    const token = localStorage.getItem('token') || localStorage.getItem('access_token');
    axios
      .get(`${API}/files/preview/${fileId}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      })
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch((e) => { if (!cancelled) setErr(e.response?.data?.detail || 'Failed to load preview'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, fileId, directUrl, officeEmbedUrl, mime, filename]);

  // When the preview payload is a .docx, pull the bytes via our own backend
  // streaming endpoint and convert to HTML in-browser using mammoth. Runs
  // entirely client-side; the file never leaves the customer's tenant.
  useEffect(() => {
    let cancelled = false;
    if (!open || !data) return undefined;
    const effectiveName = data.filename || filename || '';
    const effectiveMime = data.preview_mime || mime || '';
    if (!isDocx(effectiveMime, effectiveName)) return undefined;
    // Prefer the backend-streamed URL (guaranteed CORS + auth). Fall back
    // to the R2 presigned URL for `directUrl` / `officeEmbedUrl` callers
    // that don't have a fileId.
    const streamUrl = fileId ? `${API}/files/${fileId}/raw` : (data.download_url || directUrl);
    if (!streamUrl) return undefined;
    setDocxHtml(null);
    setDocxError('');
    setDocxLoading(true);
    (async () => {
      try {
        const token = localStorage.getItem('token') || localStorage.getItem('access_token');
        const resp = await fetch(streamUrl, {
          credentials: 'omit',
          headers: token && fileId ? { Authorization: `Bearer ${token}` } : undefined,
        });
        if (!resp.ok) throw new Error(`Failed to fetch document (${resp.status})`);
        const arrayBuffer = await resp.arrayBuffer();
        // Lazy-load mammoth so it isn't part of the initial bundle
        const mod = await import('mammoth/mammoth.browser');
        const mammoth = mod.default || mod;
        const result = await mammoth.convertToHtml({ arrayBuffer });
        if (!cancelled) setDocxHtml(result.value || '<p>(empty document)</p>');
      } catch (e) {
        if (!cancelled) setDocxError(e.message || 'Could not render document inline');
      } finally {
        if (!cancelled) setDocxLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, data, filename, mime, fileId, directUrl]);

  const displayTitle = title || filename || data?.filename || 'File preview';

  const renderBody = () => {
    if (loading) return (
      <div className="flex flex-col items-center justify-center py-16 text-slate-500">
        <Loader2 className="w-6 h-6 animate-spin mb-2" />
        <span className="text-sm">Preparing preview…</span>
      </div>
    );
    if (err) return (
      <div className="flex flex-col items-center justify-center py-14 text-slate-600">
        <FileWarning className="w-8 h-8 text-amber-500 mb-3" />
        <div className="text-sm font-medium">Preview unavailable</div>
        <div className="text-xs text-slate-500 mt-1 text-center max-w-md">{err}</div>
      </div>
    );
    if (!data) return null;

    const m = (data.preview_mime || mime || '').toLowerCase();
    const src = data.download_url || directUrl;
    const fileNameLc = (data.filename || filename || '').toLowerCase();

    // Client-side .docx render (mammoth). Never leaves the tenant.
    if (isDocx(m, fileNameLc)) {
      if (docxLoading) {
        return (
          <div className="flex flex-col items-center justify-center py-16 text-slate-500">
            <Loader2 className="w-6 h-6 animate-spin mb-2" />
            <span className="text-sm">Rendering document…</span>
          </div>
        );
      }
      if (docxHtml) {
        return (
          <div
            className="w-full h-[75vh] overflow-y-auto rounded-md bg-white border border-slate-200 p-8 prose prose-slate max-w-none"
            data-testid="file-viewer-docx-html"
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{ __html: docxHtml }}
          />
        );
      }
      // If mammoth failed we fall through to Office Online (if provided) or
      // show the error state.
      if (docxError && !data.embed_url) {
        return (
          <div className="flex flex-col items-center justify-center py-14 text-slate-600">
            <FileWarning className="w-8 h-8 text-amber-500 mb-3" />
            <div className="text-sm font-medium">Preview unavailable</div>
            <div className="text-xs text-slate-500 mt-1 text-center max-w-md">{docxError}</div>
            {src && (
              <a href={src} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline text-sm mt-3 inline-flex items-center gap-1">
                Open in new tab <ExternalLink className="w-3.5 h-3.5" />
              </a>
            )}
          </div>
        );
      }
    }

    // Microsoft Office Online viewer (server built the embed URL)
    if (data.preview_type === 'ms_office_online' && data.embed_url) {
      return (
        <div className="w-full h-[75vh] bg-slate-50 rounded-md overflow-hidden">
          <iframe
            src={data.embed_url}
            title={displayTitle}
            className="w-full h-full border-0"
            data-testid="file-viewer-office-iframe"
          />
        </div>
      );
    }

    if (isImage(m)) {
      return (
        <div className="w-full flex items-center justify-center bg-slate-50 rounded-md">
          <img src={src} alt={displayTitle} className="max-h-[75vh] max-w-full object-contain" data-testid="file-viewer-image" />
        </div>
      );
    }
    if (isVideo(m)) {
      return <video src={src} controls className="w-full max-h-[75vh]" data-testid="file-viewer-video" />;
    }
    if (isAudio(m)) {
      return <audio src={src} controls className="w-full" data-testid="file-viewer-audio" />;
    }
    if (isPdf(m) || isText(m) || (!m && /\.pdf($|\?)/i.test(src || ''))) {
      return (
        <iframe
          src={src}
          title={displayTitle}
          className="w-full h-[75vh] border-0 rounded-md bg-white"
          data-testid="file-viewer-pdf"
        />
      );
    }

    // Fallback — file type can't be rendered inline. Keep the user in
    // the modal (no new-tab link) and let them Download from the footer.
    return (
      <div className="flex flex-col items-center justify-center py-10 text-slate-600">
        <FileWarning className="w-8 h-8 text-slate-400 mb-3" />
        <div className="text-sm">Inline preview isn't supported for this file type.</div>
        <div className="text-xs text-slate-500 mt-1">Use the Download button below to open it on your device.</div>
      </div>
    );
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose && onClose()}>
      <DialogContent className="max-w-5xl w-[95vw]" data-testid="file-viewer-modal">
        <DialogHeader>
          <DialogTitle className="truncate pr-6" data-testid="file-viewer-title">{displayTitle}</DialogTitle>
        </DialogHeader>
        {renderBody()}
        <DialogFooter className="flex flex-row items-center justify-between gap-2 mt-2">
          <div className="text-xs text-slate-400">
            {docxHtml
              ? 'Rendered inline in your browser — file bytes stay in your tenant.'
              : data?.preview_type === 'ms_office_online'
              ? 'Rendered via Microsoft Office Online — file link expires in ~60 min.'
              : 'Rendered inline via signed URL — link expires in ~24 h.'}
          </div>
          <div className="flex items-center gap-2">
            {data?.download_url && (
              <a
                href={data.download_url}
                target="_blank"
                rel="noopener noreferrer"
                download={data.filename}
                className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-md border border-slate-200 hover:bg-slate-50 text-slate-700"
                data-testid="file-viewer-download"
              >
                <Download className="w-3.5 h-3.5" /> Download
              </a>
            )}
            <Button size="sm" variant="outline" onClick={onClose} data-testid="file-viewer-close">Close</Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default FileViewer;
